import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

export type SchedulerRepeatTypeParsed =
  | 'once'
  | 'every_day'
  | 'every_week'
  | 'every_month';

export interface ParsedScheduleFromTask {
  triggerType?: SchedulerRepeatTypeParsed;
  triggerTime?: Dayjs | null;
  /** 执行日期（不重复时的 DatePicker），年月日 */
  triggerDate?: Dayjs | null;
  weekdays?: number[];
  daysOfMonth?: number[];
  expireTime?: Dayjs | null;
}

const WEEK_ZH: Record<string, number> = {
  一: 1,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  日: 7,
};

/**
 * 从提示词文本解析重复类型、执行时间、星期、每月日期、到期时间，
 * 用于新建定时任务时根据提示词自动填充表单。
 */
export function parseTaskForSchedule(
  task: string,
): Partial<ParsedScheduleFromTask> {
  const t = task.trim();
  if (!t) return {};
  const result: Partial<ParsedScheduleFromTask> = {};

  // 重复类型：每天 / 每周 / 每月 / 不重复、一次（不用 \b，避免中文边界不生效）
  if (/(每天|每日)/.test(t)) result.triggerType = 'every_day';
  else if (/(每周|礼拜)/.test(t)) result.triggerType = 'every_week';
  else if (/每月/.test(t)) result.triggerType = 'every_month';
  else if (/(不重复|一次|只执行一次)/.test(t)) result.triggerType = 'once';

  // 执行时间：优先匹配「X点X分」，再匹配「X点」「X点半」、HH:mm
  const colon = t.match(/\b(\d{1,2}):(\d{2})\b/);
  const morningHM = t.match(/(?:上午|早上|早晨)\s*(\d{1,2})\s*点\s*(\d{1,2})\s*分/);
  const afternoonHM = t.match(/(?:下午)\s*(\d{1,2})\s*点\s*(\d{1,2})\s*分/);
  const eveningHM = t.match(/(?:晚上|傍晚)\s*(\d{1,2})\s*点\s*(\d{1,2})\s*分/);
  const dawnHM = t.match(/(?:凌晨)\s*(\d{1,2})\s*点\s*(\d{1,2})\s*分/);
  const onlyHourMinute = t.match(/\b(\d{1,2})\s*点\s*(\d{1,2})\s*分/);
  const morning = t.match(/(?:上午|早上|早晨)\s*(\d{1,2})\s*点(?!\s*\d)/);
  const afternoon = t.match(/(?:下午)\s*(\d{1,2})\s*点(?!\s*\d)/);
  const evening = t.match(/(?:晚上|傍晚)\s*(\d{1,2})\s*点(?!\s*\d)/);
  const dawn = t.match(/(?:凌晨)\s*(\d{1,2})\s*点(?!\s*\d)/);
  const half = t.match(/(\d{1,2})\s*点\s*半/);
  const onlyHour = t.match(/\b(\d{1,2})\s*点\b(?!\s*\d)/);

  const clampMinute = (m: number) => Math.min(59, Math.max(0, m));

  if (colon) {
    const h = Math.min(23, Math.max(0, parseInt(colon[1], 10)));
    const m = clampMinute(parseInt(colon[2], 10));
    result.triggerTime = dayjs().hour(h).minute(m).second(0).millisecond(0);
  } else if (morningHM) {
    const h = Math.min(12, Math.max(1, parseInt(morningHM[1], 10)));
    const m = clampMinute(parseInt(morningHM[2], 10));
    result.triggerTime = dayjs().hour(h).minute(m).second(0).millisecond(0);
  } else if (afternoonHM) {
    const h = Math.min(12, Math.max(1, parseInt(afternoonHM[1], 10)));
    const m = clampMinute(parseInt(afternoonHM[2], 10));
    result.triggerTime = dayjs()
      .hour(h === 12 ? 12 : h + 12)
      .minute(m)
      .second(0)
      .millisecond(0);
  } else if (eveningHM) {
    const h = Math.min(12, Math.max(1, parseInt(eveningHM[1], 10)));
    const m = clampMinute(parseInt(eveningHM[2], 10));
    result.triggerTime = dayjs()
      .hour(h === 12 ? 0 : h + 12)
      .minute(m)
      .second(0)
      .millisecond(0);
  } else if (dawnHM) {
    const h = Math.min(5, Math.max(0, parseInt(dawnHM[1], 10)));
    const m = clampMinute(parseInt(dawnHM[2], 10));
    result.triggerTime = dayjs().hour(h).minute(m).second(0).millisecond(0);
  } else if (onlyHourMinute) {
    const h = Math.min(23, Math.max(0, parseInt(onlyHourMinute[1], 10)));
    const m = clampMinute(parseInt(onlyHourMinute[2], 10));
    result.triggerTime = dayjs().hour(h).minute(m).second(0).millisecond(0);
  } else if (morning) {
    const h = Math.min(12, Math.max(1, parseInt(morning[1], 10)));
    result.triggerTime = dayjs().hour(h).minute(0).second(0).millisecond(0);
  } else if (afternoon) {
    const h = Math.min(12, Math.max(1, parseInt(afternoon[1], 10)));
    result.triggerTime = dayjs()
      .hour(h === 12 ? 12 : h + 12)
      .minute(0)
      .second(0)
      .millisecond(0);
  } else if (evening) {
    const h = Math.min(12, Math.max(1, parseInt(evening[1], 10)));
    result.triggerTime = dayjs()
      .hour(h === 12 ? 0 : h + 12)
      .minute(0)
      .second(0)
      .millisecond(0);
  } else if (dawn) {
    const h = Math.min(5, Math.max(0, parseInt(dawn[1], 10)));
    result.triggerTime = dayjs().hour(h).minute(0).second(0).millisecond(0);
  } else if (half) {
    const h = Math.min(23, Math.max(0, parseInt(half[1], 10)));
    result.triggerTime = dayjs().hour(h).minute(30).second(0).millisecond(0);
  } else if (onlyHour) {
    const h = Math.min(23, Math.max(0, parseInt(onlyHour[1], 10)));
    result.triggerTime = dayjs().hour(h).minute(0).second(0).millisecond(0);
  }

  // 执行日期（年月日）：用于不重复时的 DatePicker
  const execDateLabel = t.match(/(?:执行日期?|于|在)\s*[：:]?\s*(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日?/);
  const execDateStart = t.match(/^\s*(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日/);
  const isoDate = t.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/);
  const monthDay = t.match(/(?:执行|于|在)?\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日(?!\s*[后到期])/);
  const tomorrow = /明天/.test(t);
  const dayAfter = /后天/.test(t);
  const dayAfterTwo = /大后天/.test(t);

  const toTriggerDate = (y: number, mo: number, d: number) => {
    const parsed = dayjs().year(y).month(mo - 1).date(d).startOf('day');
    return parsed.isValid() ? parsed : null;
  };

  if (execDateLabel) {
    const y = parseInt(execDateLabel[1], 10);
    const mo = Math.min(12, Math.max(1, parseInt(execDateLabel[2], 10)));
    const d = Math.min(31, Math.max(1, parseInt(execDateLabel[3], 10)));
    const parsed = toTriggerDate(y, mo, d);
    if (parsed) result.triggerDate = parsed;
  } else if (execDateStart) {
    const y = parseInt(execDateStart[1], 10);
    const mo = Math.min(12, Math.max(1, parseInt(execDateStart[2], 10)));
    const d = Math.min(31, Math.max(1, parseInt(execDateStart[3], 10)));
    const parsed = toTriggerDate(y, mo, d);
    if (parsed) result.triggerDate = parsed;
  } else if (isoDate) {
    const y = parseInt(isoDate[1], 10);
    const mo = Math.min(12, Math.max(1, parseInt(isoDate[2], 10)));
    const d = Math.min(31, Math.max(1, parseInt(isoDate[3], 10)));
    const parsed = toTriggerDate(y, mo, d);
    if (parsed) result.triggerDate = parsed;
  } else if (monthDay) {
    const mo = Math.min(12, Math.max(1, parseInt(monthDay[1], 10)));
    const d = Math.min(31, Math.max(1, parseInt(monthDay[2], 10)));
    const parsed = dayjs().month(mo - 1).date(d).startOf('day');
    if (parsed.isValid()) result.triggerDate = parsed;
  } else if (dayAfterTwo) {
    result.triggerDate = dayjs().add(3, 'day').startOf('day');
  } else if (dayAfter) {
    result.triggerDate = dayjs().add(2, 'day').startOf('day');
  } else if (tomorrow) {
    result.triggerDate = dayjs().add(1, 'day').startOf('day');
  }

  // 每周：周X、周一二三、周一三五（支持连续多字「周一二三」及分散「周一、周三」）
  const weekMulti = t.match(/周([一二三四五六日]+)/);
  const weeks: number[] = [];
  if (weekMulti) {
    for (const char of weekMulti[1]) {
      const n = WEEK_ZH[char];
      if (n != null && !weeks.includes(n)) weeks.push(n);
    }
  }
  if (weeks.length === 0) {
    const weekMatches = t.matchAll(/周([一二三四五六日])/g);
    for (const m of weekMatches) {
      const n = WEEK_ZH[m[1]];
      if (n != null && !weeks.includes(n)) weeks.push(n);
    }
  }
  if (weeks.length) result.weekdays = weeks.sort((a, b) => a - b);

  // 每月：X号、X日、每月X号
  const dayMatches = t.matchAll(/(?:每月|每月)?(\d{1,2})[号日]/g);
  const days: number[] = [];
  for (const m of dayMatches) {
    const d = Math.min(31, Math.max(1, parseInt(m[1], 10)));
    if (!days.includes(d)) days.push(d);
  }
  if (days.length) result.daysOfMonth = days.sort((a, b) => a - b);

  // 到期时间：一年后、X年后、YYYY年M月D日
  const oneYear = t.match(/(?:一|1)\s*年\s*后/);
  const yearLater = t.match(/(\d+)\s*年\s*后/);
  const fullDate = t.match(/(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日?/);
  if (fullDate) {
    const y = parseInt(fullDate[1], 10);
    const mo = Math.min(12, Math.max(1, parseInt(fullDate[2], 10)));
    const d = Math.min(31, Math.max(1, parseInt(fullDate[3], 10)));
    const parsed = dayjs()
      .year(y)
      .month(mo - 1)
      .date(d)
      .hour(12)
      .minute(0)
      .second(0)
      .millisecond(0);
    if (parsed.isValid()) result.expireTime = parsed;
  } else if (oneYear || yearLater) {
    const n = yearLater
      ? Math.min(10, Math.max(1, parseInt(yearLater[1], 10)))
      : 1;
    result.expireTime = dayjs()
      .add(n, 'year')
      .hour(12)
      .minute(0)
      .second(0)
      .millisecond(0);
  }

  return result;
}
